/* SoWood help & policy pages (sections/sw-help-page.liquid): table of contents,
   question search, order tracking through 17TRACK, size chart tabs and units. */
(() => {
  if (window.swHelpLoaded) return;
  window.swHelpLoaded = true;

  const headerOffset = () =>
    (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--m-header-height')) || 0) + 16;

  const scrollToEl = (el) =>
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - headerOffset(), behavior: 'smooth' });

  function initToc(root) {
    const links = [...root.querySelectorAll('[data-sw-toc-link]')];
    const ids = [...new Set(links.map((a) => decodeURIComponent(a.hash.slice(1))))];
    const targets = ids.map((id) => document.getElementById(id)).filter(Boolean);
    const tab = root.querySelector('[data-sw-toc-open]');
    const sheet = root.querySelector('[data-sw-toc-sheet]');

    const setActive = () => {
      const line = headerOffset() + 40;
      let current = targets[0];
      targets.forEach((t) => {
        if (t.getBoundingClientRect().top <= line) current = t;
      });
      links.forEach((a) => a.classList.toggle('is-active', current && a.hash === `#${current.id}`));
    };
    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          setActive();
          ticking = false;
        });
      },
      { passive: true }
    );
    setActive();

    const closeSheet = () => {
      if (!sheet || sheet.hidden) return;
      sheet.classList.remove('is-open');
      document.documentElement.style.removeProperty('overflow');
      setTimeout(() => (sheet.hidden = true), 300);
    };
    const openSheet = () => {
      sheet.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(() => requestAnimationFrame(() => sheet.classList.add('is-open')));
    };

    links.forEach((a) =>
      a.addEventListener('click', (e) => {
        const target = document.getElementById(decodeURIComponent(a.hash.slice(1)));
        if (!target) return;
        e.preventDefault();
        closeSheet();
        scrollToEl(target);
        history.replaceState(null, '', a.hash);
      })
    );

    if (!tab || !sheet) return;
    tab.addEventListener('click', openSheet);
    sheet.querySelectorAll('[data-sw-toc-close]').forEach((el) => el.addEventListener('click', closeSheet));
    document.addEventListener('keydown', (e) => e.key === 'Escape' && closeSheet());
    const body = root.querySelector('.sw-help__body');
    new IntersectionObserver(([entry]) => (tab.hidden = !entry.isIntersecting)).observe(body);
  }

  function initSearch(box) {
    const input = box.querySelector('[data-sw-help-search-input]');
    const empty = box.querySelector('[data-sw-help-search-empty]');
    const items = [...document.querySelectorAll('[data-sw-qa]')];
    const norm = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const texts = items.map((d) => norm(d.textContent));
    const openByDefault = items.map((d) => d.open);

    input.addEventListener('input', () => {
      const words = norm(input.value.trim()).split(/\s+/).filter(Boolean);
      let hits = 0;
      items.forEach((d, i) => {
        const show = words.every((w) => texts[i].includes(w));
        d.hidden = !show;
        d.open = words.length ? show : openByDefault[i];
        if (show) hits += 1;
      });
      document.querySelectorAll('.sw-help-faq').forEach((group) => {
        const any = !!group.querySelector('[data-sw-qa]:not([hidden])');
        group.hidden = !any;
        const heading = group.previousElementSibling;
        if (heading && heading.matches('.sw-help-h2')) heading.hidden = !any;
      });
      empty.hidden = !words.length || hits > 0;
    });
  }

  let trackScript;
  const load17track = () =>
    (trackScript ||= new Promise((resolve, reject) => {
      if (window.YQV5) return resolve();
      const s = document.createElement('script');
      s.src = 'https://www.17track.net/externalcall.js';
      s.onload = () => (window.YQV5 ? resolve() : reject());
      s.onerror = reject;
      document.head.append(s);
    }));

  function initTrack(root) {
    const form = root.querySelector('[data-sw-track-form]');
    const input = form.querySelector('input');
    const error = root.querySelector('[data-sw-track-error]');
    const result = root.querySelector('[data-sw-track-result]');
    const frame = root.querySelector('[data-sw-track-frame]');
    const open = root.querySelector('[data-sw-track-open]');

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const num = input.value.replace(/\s+/g, '').toUpperCase();
      error.hidden = !!num;
      input.setAttribute('aria-invalid', String(!num));
      if (!num) {
        input.focus();
        return;
      }
      open.href = `https://t.17track.net/en#nums=${encodeURIComponent(num)}`;
      frame.innerHTML = '';
      result.hidden = false;
      try {
        await load17track();
        window.YQV5.trackSingle({ YQ_ContainerId: frame.id, YQ_Height: 560, YQ_Fc: '0', YQ_Lang: 'en', YQ_Num: num });
      } catch (err) {
        // Script blocked or down: the "Open on 17TRACK" link below still works.
      }
      scrollToEl(result);
    });
    input.addEventListener('input', () => {
      if (!input.value.trim()) return;
      error.hidden = true;
      input.removeAttribute('aria-invalid');
    });
  }

  // Only cells made of numbers and separators ("4.3 × 5.1", "32–58") are converted.
  const convert = (text, from, to) =>
    from === to || !/^[\d.\s×x–-]+$/.test(text.trim())
      ? text
      : text.replace(/\d+(?:\.\d+)?/g, (n) => {
          const v = from === 'in' ? n * 2.54 : n / 2.54;
          return String(Math.round(v * 10) / 10);
        });

  function initSize(card) {
    const tabs = [...card.querySelectorAll('[data-sw-size-tab]')];
    const panels = tabs.map((t) => document.getElementById(t.getAttribute('aria-controls')));
    const select = (i) =>
      tabs.forEach((t, j) => {
        t.setAttribute('aria-selected', String(i === j));
        t.tabIndex = i === j ? 0 : -1;
        panels[j].hidden = i !== j;
      });
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => select(i));
      t.addEventListener('keydown', (e) => {
        const step = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
        if (!step) return;
        const next = (i + step + tabs.length) % tabs.length;
        select(next);
        tabs[next].focus();
      });
    });

    const cells = [...card.querySelectorAll('[data-sw-measure]')];
    cells.forEach((td) => (td.dataset.base = td.textContent));
    const setUnit = (unit) =>
      panels.forEach((p) => {
        p.querySelectorAll('[data-sw-measure]').forEach((td) => (td.textContent = convert(td.dataset.base, p.dataset.unit, unit)));
        p.querySelectorAll('[data-sw-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.swUnit === unit)));
      });
    card.addEventListener('click', (e) => {
      const b = e.target.closest('[data-sw-unit]');
      if (b) setUnit(b.dataset.swUnit);
    });
    select(0);
    if (panels[0]) setUnit(panels[0].dataset.unit);
  }

  const once = (selector, init) =>
    document.querySelectorAll(selector).forEach((el) => {
      if (el.dataset.swInit) return;
      el.dataset.swInit = '1';
      init(el);
    });

  function start() {
    once('[data-sw-help-toc]', initToc);
    once('[data-sw-help-search]', initSearch);
    once('[data-sw-track]', initTrack);
    once('[data-sw-size]', initSize);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
